import { useState } from "react";
import {
  Home, Phone, ClipboardCheck, Compass, Building2, Truck,
  Calendar, Mail, MessageSquare, FileText, DollarSign, Settings,
  Briefcase, GraduationCap, Shield, Crown, ChevronDown,
} from "lucide-react";

const NAV_ITEMS = [
  { id: "home", label: "Accueil", icon: Home, group: null },
  // Opérationnel
  { id: "r1", label: "R1", icon: ClipboardCheck, group: "ops", color: "#34C759" },
  { id: "r2", label: "R2", icon: Compass, group: "ops", color: "#0EA5E9" },
  { id: "chantiers", label: "Chantiers", icon: Truck, group: "ops", color: "#F97316" },
  // Clients
  { id: "telepro", label: "Telepro", icon: Phone, group: "clients", color: "#3B82F6" },
  { id: "marketing", label: "Marketing", icon: MessageSquare, group: "clients", color: "#EC4899" },
  { id: "grands_comptes", label: "Grands Comptes", icon: Briefcase, group: "clients", color: "#F59E0B" },
  { id: "communications_ia", label: "Com. IA", icon: Mail, group: "clients", color: "#0EA5E9" },
  { id: "calendrier", label: "Calendrier", icon: Calendar, group: "clients", color: "#14B8A6" },
  // Admin & RH
  { id: "administratif", label: "Admin", icon: FileText, group: "admin", color: "#94A3B8" },
  { id: "amo", label: "AMO", icon: Building2, group: "admin", color: "#A855F7" },
  { id: "formations", label: "Formations", icon: GraduationCap, group: "admin", color: "#D946EF" },
  // Finances
  { id: "compta_finances", label: "Compta", icon: DollarSign, group: "finance", color: "#10B981" },
  { id: "compta_fi_new", label: "Compta NEW", icon: DollarSign, group: "finance", color: "#F97316" },
  // Direction
  { id: "comite_direction", label: "Comite Dir.", icon: Crown, group: "direction", color: "#F59E0B" },
  { id: "super_batard", label: "Super Batard", icon: Shield, group: "direction", color: "#EF4444", adminOnly: true },
  { id: "tests", label: "Tests", icon: Settings, group: "direction", color: "#8B5CF6", adminOnly: true },
];

export default function DashboardNav({ current, onNavigate, authUser }) {
  const [moreOpen, setMoreOpen] = useState(false);

  const isAdmin = authUser?.email === "sdi@sani-adapt.fr" || authUser?.role === "admin";

  // Items visibles directement : accueil + les plus utilisés
  const primaryIds = ["home", "telepro", "r1", "r2", "calendrier", "communications_ia", "administratif", "compta_finances"];
  const primary = NAV_ITEMS.filter(i => primaryIds.includes(i.id) && (!i.adminOnly || isAdmin));
  const secondary = NAV_ITEMS.filter(i => !primaryIds.includes(i.id) && (!i.adminOnly || isAdmin));

  const handleClick = (id) => {
    setMoreOpen(false);
    if (id === current) return;
    onNavigate(id);
  };

  return (
    <nav
      data-testid="dashboard-nav"
      className="bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/60 px-4 py-0 flex items-center gap-0.5 relative z-50"
      style={{ minHeight: 42 }}
    >
      {primary.map((item) => {
        const Icon = item.icon;
        const isActive = item.id === current;
        return (
          <button
            key={item.id}
            data-testid={`nav-${item.id}`}
            onClick={() => handleClick(item.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-all whitespace-nowrap ${
              isActive
                ? "bg-white/10 text-white"
                : "text-slate-500 hover:text-slate-300 hover:bg-white/5"
            }`}
          >
            <Icon className="w-3.5 h-3.5" style={isActive && item.color ? { color: item.color } : {}} />
            {item.label}
            {isActive && (
              <span
                className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                style={{ backgroundColor: item.color || "#fff" }}
              />
            )}
          </button>
        );
      })}

      {/* More dropdown */}
      <div className="relative ml-auto">
        <button
          data-testid="nav-more-btn"
          onClick={() => setMoreOpen(!moreOpen)}
          className={`flex items-center gap-1 px-3 py-2 text-xs font-medium rounded-lg transition-all ${
            moreOpen || secondary.some(s => s.id === current)
              ? "bg-white/10 text-white"
              : "text-slate-500 hover:text-slate-300 hover:bg-white/5"
          }`}
        >
          Plus
          <ChevronDown className={`w-3 h-3 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
        </button>

        {moreOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setMoreOpen(false)} />
            <div
              data-testid="nav-more-dropdown"
              className="absolute right-0 top-full mt-1 w-56 bg-slate-900 border border-slate-700/60 rounded-xl shadow-2xl shadow-black/40 py-1 z-50 overflow-hidden"
            >
              {["ops", "clients", "admin", "finance", "direction"].map((group) => {
                const items = secondary.filter((i) => i.group === group);
                if (items.length === 0) return null;
                const groupLabels = {
                  ops: "Terrain",
                  clients: "Clients",
                  admin: "Admin & RH",
                  finance: "Finances",
                  direction: "Direction",
                };
                return (
                  <div key={group}>
                    <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-600 font-bold">
                      {groupLabels[group]}
                    </div>
                    {items.map((item) => {
                      const Icon = item.icon;
                      const isActive = item.id === current;
                      return (
                        <button
                          key={item.id}
                          data-testid={`nav-${item.id}`}
                          onClick={() => handleClick(item.id)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-colors ${
                            isActive
                              ? "bg-white/10 text-white"
                              : "text-slate-400 hover:text-white hover:bg-white/5"
                          }`}
                        >
                          <Icon className="w-3.5 h-3.5" style={{ color: item.color }} />
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </nav>
  );
}
